import { ApiError } from "@/lib/api-client";

// Maps a thrown client_records error to a UI outcome (mirror of
// sign-in/auth-error.ts + legacy-signup/legacy-signup-error.ts). We branch ONLY on the
// structured `error.code` / `error.status` and the 422 `loc`/`type` — NEVER on the
// server's `msg`/`input`. For create-client the rejected inputs ARE PHI (names /
// DOB); the server scrubs them to "***", but to be safe we never echo any
// server-supplied string and always render our own copy.
//
// [Source: src/application/common/exceptions.py — ClientPhiServiceUnavailable
//  (PHI-503), ClientPhiMisconfigured (PHI-500); create_client/schemas.py —
//  future_dob / implausible_dob / string_too_short.]

export interface ClientErrorBanner {
  variant: "warn" | "danger";
  message: string;
}

/** Inline messages keyed to the intake form's RHF field names. */
export interface ClientFieldErrors {
  firstName?: string;
  lastName?: string;
  dob?: string;
}

export interface MappedCreateError {
  /** Banner descriptor, or null when the failure is fully handled inline. */
  banner: ClientErrorBanner | null;
  /** Per-field messages to attach via `form.setError` without dropping form state. */
  fieldErrors: ClientFieldErrors;
}

const GENERIC_CREATE: ClientErrorBanner = {
  variant: "danger",
  message: "Something went wrong creating the client record. Please try again.",
};

const PHI_UNAVAILABLE: ClientErrorBanner = {
  variant: "warn",
  message: "Secure storage is temporarily unavailable. Please try again in a moment.",
};

// Story 13.6 — 403 BILL-001 (subscription gate): a PERMANENT read-only refusal, so
// the copy must not suggest retrying. Source-neutral by design: one code covers org
// and individual lapse, and B2B renewal happens off-platform (CITS) — never a
// renewal CTA here. The Profile page carries the discriminated detail.
const SUBSCRIPTION_READ_ONLY: ClientErrorBanner = {
  variant: "warn",
  message:
    "Your access is currently read-only, so new client records can't be created. See your Profile for details.",
};

// Translate a single 422 detail item into our own (PHI-free) field message. We read
// the field from the END of `loc` (e.g. ["body","dob"] → "dob") and the validator
// `type` for the DOB sub-cases — never the server's message text.
function applyDetailItem(item: unknown, into: ClientFieldErrors): void {
  if (!item || typeof item !== "object") return;
  const loc = (item as { loc?: unknown }).loc;
  const type = (item as { type?: unknown }).type;
  const field = Array.isArray(loc) && loc.length > 0 ? String(loc[loc.length - 1]) : "";

  if (field === "first_name" && !into.firstName) {
    into.firstName = "Enter the client's first name (1–100 characters).";
  } else if (field === "last_name" && !into.lastName) {
    into.lastName = "Enter the client's last name (1–100 characters).";
  } else if (field === "dob" && !into.dob) {
    into.dob =
      type === "future_dob"
        ? "Date of birth cannot be in the future."
        : type === "implausible_dob"
          ? "Date of birth must be on or after 1900-01-01."
          : "Enter a valid date of birth.";
  }
}

function parse422Details(details: unknown): ClientFieldErrors {
  const fieldErrors: ClientFieldErrors = {};
  if (Array.isArray(details)) {
    for (const item of details) applyDetailItem(item, fieldErrors);
  }
  return fieldErrors;
}

/** Map a create-client failure to inline field errors + an optional banner. */
export function toCreateClientError(error: unknown): MappedCreateError {
  if (error instanceof ApiError) {
    if (error.status === 422) {
      const fieldErrors = parse422Details(error.details);
      const handledInline = Object.keys(fieldErrors).length > 0;
      return {
        fieldErrors,
        // Only show a banner when nothing could be attached to a field, so the user
        // is not told "check your details" with no field actually flagged.
        banner: handledInline
          ? null
          : { variant: "warn", message: "Please check the client details and try again." },
      };
    }
    if (error.code === "PHI-503") {
      return { fieldErrors: {}, banner: PHI_UNAVAILABLE };
    }
    if (error.code === "BILL-001") {
      return { fieldErrors: {}, banner: SUBSCRIPTION_READ_ONLY };
    }
    // PHI-500 and any other coded error fall through to the generic danger banner.
  }
  // NETWORK_ERROR / AuthExpiredError / unknown → generic, no internals leaked.
  return { fieldErrors: {}, banner: GENERIC_CREATE };
}

/** Short, generic message for a failed list load (AC3 error state). */
export function toListClientsErrorMessage(): string {
  return "We couldn't load your clients. Please try again.";
}

/** Short, generic message for a failed reveal (AC4 — non-destructive, row stays masked). */
export function toRevealNameErrorMessage(): string {
  return "Couldn't reveal this name. Please try again.";
}

/**
 * True for the permanent GDPR Art. 17 erasure response (spec-gh-376). The backend
 * raises `410 Gone` / `ADMIN-004` when a reveal targets a crypto-shredded client —
 * the PHI is gone for good, so this is a TERMINAL, non-retryable state, never the
 * generic "try again". We branch on the structured code/status only (never `msg`),
 * matching the policy in {@link toCreateClientError}.
 */
export function isGdprErasedError(error: unknown): boolean {
  // `code` is the structured signal; the `status === 410` fallback also latches proxy/gateway 410s that arrive without our ADMIN-004 body (defense-in-depth).
  return error instanceof ApiError && (error.code === "ADMIN-004" || error.status === 410);
}
